'use client';
import React, { useEffect, useState } from 'react';
import {
  CheckCircle, Calendar, Clock, User, Package, DollarSign,
  ArrowRight, Sparkles, X, Mail, Phone, MapPin,
} from 'lucide-react';
import Link from 'next/link';

export interface DepositSuccessData {
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  eventType: string;
  eventDate: string;
  eventTime?: string;
  venue?: string;
  services: string[];
  totalCost: number;
  depositPaid: number;
  balanceDue: number;
  balanceDueDate: string;
  vendorName?: string;
  eventId?: string;
}

interface DepositSuccessConfirmationProps {
  data: DepositSuccessData;
  onClose?: () => void;
  /** If true, renders as a full-page section instead of a modal overlay */
  inline?: boolean;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'TBD';
  if (dateStr.includes(',')) return dateStr;
  const parts = dateStr.split('-').map(Number);
  if (parts.length < 3) return dateStr;
  const [year, month, day] = parts;
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[month - 1]} ${day}, ${year}`;
}

const NEXT_STEPS = [
  {
    icon: '✅',
    title: 'Deposit Confirmed',
    detail: 'Your 25% deposit has been received and your date is now secured.',
    status: 'done',
  },
  {
    icon: '📋',
    title: 'Contract Review',
    detail: 'Review and sign your event service contract in the client portal.',
    status: 'next',
  },
  {
    icon: '🎧',
    title: 'Vendor Coordination',
    detail: 'Your assigned vendor will reach out within 24 hours to confirm details.',
    status: 'upcoming',
  },
  {
    icon: '🎵',
    title: 'Build Your Playlist',
    detail: 'Submit song requests and event timeline in your client portal.',
    status: 'upcoming',
  },
  {
    icon: '💳',
    title: 'Balance Payment',
    detail: `Remaining balance due 14 days before your event.`,
    status: 'upcoming',
  },
  {
    icon: '🎉',
    title: 'Event Day',
    detail: 'Sit back and enjoy — your team handles everything.',
    status: 'upcoming',
  },
];

export default function DepositSuccessConfirmation({
  data,
  onClose,
  inline = false,
}: DepositSuccessConfirmationProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const signupHref = `/sign-up-login-screen?role=client&email=${encodeURIComponent(data.clientEmail)}`;

  const content = (
    <div
      className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${
        inline ? 'w-full' : 'relative w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto scrollbar-dark rounded-2xl neon-border-gold card-glass shadow-2xl'
      }`}
    >
      {/* Header */}
      <div className="relative overflow-hidden rounded-t-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border-b border-primary/20 px-6 pt-8 pb-6">
        {/* Decorative glow */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-accent/10 blur-2xl pointer-events-none" />

        {!inline && onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-muted/40 hover:bg-muted/70 flex items-center justify-center transition-colors z-10"
          >
            <X size={15} className="text-muted-foreground" />
          </button>
        )}

        <div className="flex flex-col items-center text-center gap-3 relative z-10">
          <div className="w-16 h-16 rounded-full bg-green-500/15 border-2 border-green-500/50 flex items-center justify-center shadow-lg shadow-green-500/10">
            <CheckCircle size={32} className="text-green-400" />
          </div>
          <div>
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Sparkles size={13} className="text-primary" />
              <span className="text-xs font-mono tracking-[0.3em] text-primary uppercase">Booking Confirmed</span>
              <Sparkles size={13} className="text-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold gold-gradient-text">Your Date is Locked In!</h2>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
              Deposit received. Here&apos;s everything you need to know about your upcoming event.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-5">
        {/* Event Summary Card */}
        <div className="rounded-xl card-glass border border-border p-5 space-y-4">
          <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Calendar size={12} className="text-primary" /> Event Details
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-wider mb-0.5">Client</p>
              <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <User size={12} className="text-primary/60 shrink-0" />
                {data.clientName}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-wider mb-0.5">Event Type</p>
              <p className="text-sm font-semibold text-foreground">{data.eventType}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-wider mb-0.5">Event Date</p>
              <p className="text-sm font-bold text-primary flex items-center gap-1.5">
                <Calendar size={12} className="shrink-0" />
                {formatDate(data.eventDate)}
              </p>
            </div>
            {data.eventTime && (
              <div>
                <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-wider mb-0.5">Start Time</p>
                <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  <Clock size={12} className="text-primary/60 shrink-0" />
                  {data.eventTime}
                </p>
              </div>
            )}
            {data.venue && (
              <div className="col-span-2">
                <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-wider mb-0.5">Venue</p>
                <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  <MapPin size={12} className="text-primary/60 shrink-0" />
                  {data.venue}
                </p>
              </div>
            )}
            {data.vendorName && (
              <div className="col-span-2">
                <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-wider mb-0.5">Assigned DJ / Vendor</p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-primary">{data.vendorName.charAt(0)}</span>
                  </div>
                  <p className="text-sm font-bold text-foreground">{data.vendorName}</p>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/10 text-green-400 border border-green-500/30">Confirmed</span>
                </div>
              </div>
            )}
          </div>

          {/* Contact row */}
          <div className="pt-3 border-t border-border/40 flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail size={11} className="text-primary/60 shrink-0" />
              <span className="truncate max-w-[180px]">{data.clientEmail}</span>
            </div>
            {data.clientPhone && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Phone size={11} className="text-primary/60 shrink-0" />
                <span>{data.clientPhone}</span>
              </div>
            )}
          </div>
        </div>

        {/* Package Details */}
        <div className="rounded-xl card-glass border border-border p-5 space-y-3">
          <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Package size={12} className="text-primary" /> Package Details
          </h3>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">DJ Services (Base)</span>
              <span className="font-mono font-semibold text-foreground">$1,200</span>
            </div>
            {data.services.map(svc => (
              <div key={svc} className="flex justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-accent inline-block" />
                  {svc}
                </span>
                <span className="font-mono text-foreground">Included</span>
              </div>
            ))}
          </div>

          <div className="border-t border-border/50 pt-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-foreground flex items-center gap-1.5">
                <DollarSign size={13} className="text-primary" /> Total Package
              </span>
              <span className="text-lg font-extrabold gold-gradient-text font-mono">${data.totalCost.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-green-400 font-semibold flex items-center gap-1.5">
                <CheckCircle size={12} /> Deposit Paid
              </span>
              <span className="font-mono font-bold text-green-400">${data.depositPaid.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Balance Due</span>
              <span className="font-mono font-semibold text-foreground">${data.balanceDue.toLocaleString()}</span>
            </div>
            <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Clock size={11} className="text-primary/70" />
                <span className="text-xs text-muted-foreground">Balance due by</span>
              </div>
              <span className="text-xs font-bold text-primary font-mono">{formatDate(data.balanceDueDate)}</span>
            </div>
          </div>
        </div>

        {/* Next Steps Timeline */}
        <div className="rounded-xl card-glass border border-border p-5 space-y-4">
          <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider">What Happens Next</h3>
          <div className="space-y-0">
            {NEXT_STEPS.map((step, i) => (
              <div key={i} className="flex gap-3 group">
                {/* Connector */}
                <div className="flex flex-col items-center shrink-0">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-base shrink-0 border transition-all ${
                      step.status === 'done'
                        ? 'bg-green-500/15 border-green-500/40'
                        : step.status === 'next' ?'bg-primary/15 border-primary/40' :'bg-muted/30 border-border'
                    }`}
                  >
                    {step.icon}
                  </div>
                  {i < NEXT_STEPS.length - 1 && (
                    <div className={`w-px flex-1 my-1 min-h-[16px] ${step.status === 'done' ? 'bg-green-500/30' : 'bg-border/50'}`} />
                  )}
                </div>
                {/* Content */}
                <div className="pb-4 pt-1 flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className={`text-sm font-bold ${step.status === 'done' ? 'text-green-400' : step.status === 'next' ? 'text-primary' : 'text-foreground'}`}>
                      {step.title}
                    </p>
                    {step.status === 'done' && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-green-500/10 text-green-400 border border-green-500/25">Done</span>
                    )}
                    {step.status === 'next' && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-primary/10 text-primary border border-primary/25">Up Next</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-xl bg-gradient-to-br from-primary/10 to-accent/5 border border-primary/25 p-5 space-y-3">
          <p className="text-sm font-semibold text-foreground">Track your booking in the client portal</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Sign in or create an account with <span className="text-foreground font-medium">{data.clientEmail}</span> to manage payments, sign your contract, build your playlist, and message your vendor.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              href={signupHref}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold gold-gradient text-black hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              Open Client Portal
              <ArrowRight size={14} />
            </Link>
            {onClose && (
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all"
              >
                Close
              </button>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground/50 text-center font-mono">
          A confirmation email has been sent to {data.clientEmail}
        </p>
      </div>
    </div>
  );

  if (inline) return <section className="py-12">{content}</section>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl z-10">
        {content}
      </div>
    </div>
  );
}
